import { useEffect, useState, useRef } from "react";
import { useFetch } from "../../../hooks/useFetch"
import { API_ENDPOINTS, POPULATE_PARAMS } from "../../../config/constants"
import Loading from '../../../components/common/Loading'
import ErrorMessage from '../../../components/common/ErrorMessage'
import { getMediaURL } from "../../../utils/helpers"

function MemberCard({ member, index }) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
            { threshold: 0.15 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    // FIX 1: was referencing undefined `members` — should be the `member` prop
    // FIX 2: guard against missing cover before calling getMediaURL
    const imageUrl = member.cover ? getMediaURL(member.cover) : '/placeholder_hor.jpg';

    return (
        <div
            ref={ref}
            className="flex flex-col items-center text-center group"
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 0.6s ease ${(index % 4) * 80}ms, transform 0.6s ease ${(index % 4) * 80}ms`,
            }}
        >
            {/* Photo */}
            <div className="relative mb-4">
                <div className="absolute inset-0 rounded-full bg-blue-100 scale-110 blur-md opacity-0 group-hover:opacity-70 transition-opacity duration-500" />
                <img
                    src={imageUrl}
                    alt={member.name}
                    onError={(e) => {
                        e.target.src = `https://placehold.co/160x160/1e3a5f/ffffff?text=${encodeURIComponent(member.name.split(" ").pop())}`;
                    }}
                    className="relative w-32 h-32 rounded-full object-cover border-4 border-white shadow-md group-hover:shadow-xl transition-shadow duration-400"
                />
            </div>

            {/* Name */}
            <p className="text-sm font-serif font-bold text-blue-900 leading-snug">
                {member.name}
            </p>

            {/* Role */}
            <p className="mt-1 text-xs tracking-wider uppercase text-blue-600/60 font-medium">
                {member.position}
            </p>
        </div>
    );
}

const Directors = () => {
    const { data: members, loading, error } = useFetch(
        API_ENDPOINTS.DIRECTORS,
        POPULATE_PARAMS.ONE_WITH_IMAGES
    );

    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    // FIX 3: loading/error were unhandled — members.map() would crash before data arrives
    if (loading) return <Loading />;
    if (error) return <ErrorMessage message={error} />;

    return (
        <div className="w-full min-h-screen bg-white">
            <div className="max-w-5xl mx-auto px-4 pt-20 pb-28">

                {/* ── HEADER ── */}
                <div className="text-center mb-16">
                    <p
                        className="text-xs tracking-[0.25em] uppercase text-blue-600/60 font-semibold mb-3"
                        style={{
                            opacity: visible ? 1 : 0,
                            transition: "opacity 0.6s ease",
                        }}
                    >
                        Kurul
                    </p>
                    <h1
                        className="text-3xl font-serif font-bold text-blue-900"
                        style={{
                            opacity: visible ? 1 : 0,
                            transform: visible ? "translateY(0)" : "translateY(14px)",
                            transition: "opacity 0.7s ease 120ms, transform 0.7s ease 120ms",
                        }}
                    >
                        Yönetim Kurulu
                    </h1>
                    <div
                        className="mx-auto mt-5 w-10 h-px bg-blue-900/20"
                        style={{
                            opacity: visible ? 1 : 0,
                            transition: "opacity 0.7s ease 280ms",
                        }}
                    />
                </div>

                {/* ── GRID ── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-8 gap-y-14">
                    {members.map((member, i) => (
                        <MemberCard key={member.id} member={member} index={i} />
                    ))}
                </div>

            </div>
        </div>
    );
};

export default Directors;